// src/components/reservation/reservationFlow.ui.js
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ArrowRight, ChevronDown, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { fromZonedTime, formatInTimeZone } from "date-fns-tz";

import {
  safeStr,
  uniq,
  parseLocalDT,
  buildLocalDT,
  clampToMinInZone,
  formatDateTimeNice,
  addHoursLocalInputInZone,
  calcPolicyDeposit,
  formatCarNameWithoutYear,
} from "./reservationFlow.logic";

const RF = {
  ink: "#151915",
  muted: "#687067",
  subtle: "#7b8379",
  line: "#d8dbd2",
  lineStrong: "#bcc2b8",
  surface: "#ffffff",
  surfaceSoft: "#f3f1ea",
  green: "#7ed957",
  greenDark: "#7d9f3b",
  greenMid: "#67c945",
  greenText: "#151915",
  greenSoft: "#f0f7e3",
  greenLine: "#bdd38f",
  warnBg: "#fff7e6",
  warnLine: "#f4d08b",
  warnText: "#7a4b05",
  dangerBg: "#fff4f4",
  dangerLine: "#fecaca",
  dangerText: "#8a1f1f",
  shadowSm: "0 8px 22px rgba(22,29,22,0.07)",
  shadowMd: "0 22px 50px rgba(22,29,22,0.14)",
};

/* ========================= portal pos helper ========================= */
function usePortalPosition(open, anchorRef, deps = []) {
  const [pos, setPos] = useState(null);

  const updatePos = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || r.right;
    const margin = 12;
    const maxWidth = Math.max(260, viewportWidth - margin * 2);
    const width = Math.min(Math.max(r.width, 280), maxWidth);
    const left = Math.min(Math.max(margin, r.left), Math.max(margin, viewportWidth - width - margin));
    setPos({ left, top: r.bottom + 8, width });
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ...deps]);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => updatePos();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, updatePos]);

  return pos;
}

function normalizeSelectOptions(options) {
  if (!Array.isArray(options)) return [];
  if (!options.length) return [];
  if (typeof options[0] === "string") return options.map((v) => ({ value: v, label: v }));
  return options
    .map((o) => ({
      value: safeStr(o?.value ?? o?.code ?? o?.id ?? o?.label),
      label: safeStr(o?.label ?? o?.value ?? o?.code ?? o?.id),
      disabled: !!o?.disabled,
    }))
    .filter((o) => o.value);
}

/* ========================= SelectPopover ========================= */
export function SelectPopover({
  labelText,
  value,
  onChange,
  options,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  disabled = false,
  searchable = true,
  compact = false,
  maxMenuHeight = 260,
}) {
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const opts = useMemo(() => normalizeSelectOptions(options), [options]);
  const pos = usePortalPosition(open && !disabled, wrapRef, [q, value, opts.length]);

  const filtered = useMemo(() => {
    if (!searchable) return opts;
    const nq = safeStr(q).toLowerCase();
    if (!nq) return opts;
    return opts.filter(
      (o) => o.label.toLowerCase().includes(nq) || o.value.toLowerCase().includes(nq)
    );
  }, [opts, q, searchable]);

  const selected = useMemo(() => {
    const v = safeStr(value);
    return opts.find((o) => safeStr(o.value) === v) || null;
  }, [opts, value]);

  useEffect(() => {
    function onDoc(e) {
      const w = wrapRef.current;
      const m = menuRef.current;
      if (!w) return;
      if (w.contains(e.target)) return;
      if (m && m.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, []);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const menu =
    open && !disabled && pos
      ? createPortal(
          <div
            className="rf-popover-menu"
            ref={menuRef}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width: pos.width,
              zIndex: 999999,
              border: `1px solid ${RF.line}`,
              borderRadius: 8,
              background: RF.surface,
              boxShadow: RF.shadowMd,
              overflow: "hidden",
            }}
          >
            {searchable ? (
              <div style={{ padding: 10, borderBottom: `1px solid ${RF.line}` }}>
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder={searchPlaceholder}
                  style={{ ...inputStyle, height: 40, borderRadius: 8 }}
                  autoFocus
                />
              </div>
            ) : null}

            <div style={{ maxHeight: maxMenuHeight, overflow: "auto" }}>
              {filtered.length ? (
                filtered.map((opt) => {
                  const isActive = safeStr(opt.value) === safeStr(value);
                  return (
                    <button
                      className={`rf-popover-option ${isActive ? "is-active" : ""}`}
                      key={opt.value}
                      type="button"
                      disabled={!!opt.disabled}
                      onClick={() => {
                        if (opt.disabled) return;
                        onChange(opt.value);
                        setOpen(false);
                      }}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 12px",
                        border: "none",
                        background: isActive ? RF.greenSoft : RF.surface,
                        color: isActive ? RF.greenText : RF.ink,
                        fontWeight: 900,
                        cursor: opt.disabled ? "not-allowed" : "pointer",
                        opacity: opt.disabled ? 0.5 : 1,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })
              ) : (
                <div style={{ padding: 12, fontSize: 13, color: RF.muted, fontWeight: 800 }}>
                  No matches.
                </div>
              )}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div style={{ display: "grid", gap: 6 }} ref={wrapRef}>
      {labelText ? <div style={label}>{labelText}</div> : null}

      <button
        className={`rf-select-trigger ${compact ? "is-compact" : ""}`}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...(compact
            ? {
                height: 38,
                borderRadius: 8,
                border: `1px solid ${RF.line}`,
                background: RF.surface,
                padding: "0 10px",
                fontSize: 13,
              }
            : inputStyle),
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          textAlign: "left",
          paddingRight: 10,
        }}
      >
        <span style={{ color: selected ? RF.ink : RF.subtle, fontWeight: 850, fontSize: 13 }}>
          {selected?.label || placeholder}
        </span>
        <ChevronDown
          size={16}
          style={{ flexShrink: 0, opacity: 0.8, transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 180ms ease" }}
        />
      </button>

      {menu}
    </div>
  );
}

/* ========================= FancyDateTimePicker ========================= */
export function FancyDateTimePicker({
  labelText,
  value,
  onChange,
  min,
  placeholder = "Select…",
  disabled = false,
  tz = "UTC",
}) {
  const wrapRef = useRef(null);
  const popRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const current = useMemo(() => parseLocalDT(value), [value]);
  const [draftDate, setDraftDate] = useState(current.date);
  const [draftTime, setDraftTime] = useState(current.time || "10:00");

  useEffect(() => {
    const p = parseLocalDT(value);
    setDraftDate(p.date);
    setDraftTime(p.time || "10:00");
  }, [value]);

  const updatePos = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || r.right;
    const margin = 12;
    const maxWidth = Math.max(280, viewportWidth - margin * 2);
    const width = Math.min(Math.max(r.width, 360), maxWidth);
    const left = Math.min(Math.max(margin, r.left), Math.max(margin, viewportWidth - width - margin));
    setPos({ left, top: r.bottom + 8, width });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, value, draftDate, draftTime]);

  useEffect(() => {
    function onDoc(e) {
      const w = wrapRef.current;
      const p = popRef.current;
      if (!w) return;
      if (w.contains(e.target)) return;
      if (p && p.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => updatePos();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, updatePos]);

  const minParts = useMemo(() => parseLocalDT(min), [min]);
  const minDate = minParts.date || "";
  const minTimeSameDay = minParts.time || "";

  const displayText = useMemo(() => {
    if (!safeStr(value)) return placeholder;
    return formatDateTimeNice(value, tz);
  }, [value, placeholder, tz]);

  const apply = () => {
    const nextRaw = buildLocalDT(draftDate, draftTime);
    const next = clampToMinInZone(nextRaw, min, tz);
    if (safeStr(next)) onChange(next);
    setOpen(false);
  };

  const popover =
    open && !disabled && pos
      ? createPortal(
          <div
            className="rf-popover-menu"
            ref={popRef}
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width: pos.width,
              zIndex: 999999,
              border: `1px solid ${RF.line}`,
              borderRadius: 8,
              background: RF.surface,
              boxShadow: RF.shadowMd,
              overflow: "auto",
              maxHeight: "calc(100vh - 24px)",
            }}
          >
            <div style={{ padding: 12, borderBottom: `1px solid ${RF.line}` }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", fontWeight: 950, color: RF.ink }}>
                <CalendarDays size={16} /> {labelText || "Date & time"}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: RF.muted, fontWeight: 800 }}>
                Timezone: {tz}
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: RF.muted, fontWeight: 700, lineHeight: 1.4 }}>
                Minimum: {safeStr(min) ? formatDateTimeNice(min, tz) : "—"}
              </div>
            </div>

            <div style={{ padding: 12, display: "grid", gap: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: pos.width < 340 ? "1fr" : "minmax(0, 1fr) 140px", gap: 10 }}>
                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "#374151" }}>Date</div>
                  <input
                    type="date"
                    value={draftDate}
                    min={minDate || undefined}
                    onChange={(e) => setDraftDate(e.target.value)}
                    style={{ ...inputStyle, height: 44, borderRadius: 8 }}
                    autoFocus
                  />
                </div>

                <div style={{ display: "grid", gap: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 900, color: "#374151" }}>Time</div>
                  <input
                    type="time"
                    value={draftTime}
                    min={draftDate && minDate && draftDate === minDate ? (minTimeSameDay || undefined) : undefined}
                    onChange={(e) => setDraftTime(e.target.value)}
                    style={{ ...inputStyle, height: 44, borderRadius: 8 }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="button" onClick={() => setOpen(false)} style={{ ...ctaSecondary, borderRadius: 8 }}>
                  Cancel
                </button>
                <button type="button" onClick={apply} style={{ ...ctaPrimary, borderRadius: 8 }}>
                  Apply <ArrowRight size={16} />
                </button>
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div style={{ display: "grid", gap: 6 }} ref={wrapRef}>
      {labelText ? <div style={label}>{labelText}</div> : null}

      <button
        className="rf-select-trigger"
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          ...inputStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          textAlign: "left",
          paddingRight: 10,
        }}
      >
        <span style={{ color: safeStr(value) ? RF.ink : RF.subtle, fontWeight: 850, fontSize: 13 }}>
          {displayText}
        </span>
        <CalendarDays size={16} style={{ opacity: 0.8 }} />
      </button>

      {popover}
    </div>
  );
}

/* ========================= calendar helpers ========================= */
const WEEKDAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];
const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function pad2(n) {
  return String(n).padStart(2, "0");
}

function ymdFromParts(y, mIndex, d) {
  return `${y}-${pad2(mIndex + 1)}-${pad2(d)}`;
}

function compareYMD(a, b) {
  if (!a || !b) return 0;
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

function parseYMDParts(ymd) {
  const [y, m, d] = safeStr(ymd).split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m: m - 1, d };
}

function daysInMonth(y, mIndex) {
  return new Date(y, mIndex + 1, 0).getDate();
}

function startWeekdayMon(y, mIndex) {
  const dow = new Date(y, mIndex, 1).getDay();
  return dow === 0 ? 6 : dow - 1;
}

function isBetweenYMD(dateStr, startStr, endStr) {
  if (!dateStr || !startStr || !endStr) return false;
  return compareYMD(dateStr, startStr) >= 0 && compareYMD(dateStr, endStr) <= 0;
}

function formatDateCompact(localValue, tz) {
  if (!safeStr(localValue)) return "—";
  try {
    const utc = fromZonedTime(localValue, tz);
    return formatInTimeZone(utc, tz, "MMM d, HH:mm");
  } catch {
    return parseLocalDT(localValue).date || "—";
  }
}

/* ========================= FancyDateRangePicker ========================= */
export function FancyDateRangePicker({
  labelText = "Trip dates",
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  startMin,
  endMin,
  minRentalDays = 3,
  disabled = false,
  tz = "UTC",
}) {
  const wrapRef = useRef(null);
  const triggerRef = useRef(null);
  const popRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);

  const startParts = useMemo(() => parseLocalDT(startValue), [startValue]);
  const endParts = useMemo(() => parseLocalDT(endValue), [endValue]);
  const startMinParts = useMemo(() => parseLocalDT(startMin), [startMin]);

  const [draftStartDate, setDraftStartDate] = useState(startParts.date);
  const [draftEndDate, setDraftEndDate] = useState(endParts.date);
  const [draftStartTime, setDraftStartTime] = useState(startParts.time || "10:00");
  const [draftEndTime, setDraftEndTime] = useState(endParts.time || "10:00");
  const [hoverDate, setHoverDate] = useState("");

  const initialView = useMemo(() => {
    const seed = parseYMDParts(startParts.date) || parseYMDParts(startMinParts.date) || { y: new Date().getFullYear(), m: new Date().getMonth() };
    return { y: seed.y, m: seed.m };
  }, [startParts.date, startMinParts.date]);

  const [viewYear, setViewYear] = useState(initialView.y);
  const [viewMonth, setViewMonth] = useState(initialView.m);

  useEffect(() => {
    if (!open) return;
    const pStart = parseLocalDT(startValue);
    const pEnd = parseLocalDT(endValue);
    setDraftStartDate(pStart.date);
    setDraftEndDate(pEnd.date);
    setDraftStartTime(pStart.time || "10:00");
    setDraftEndTime(pEnd.time || "10:00");
    setHoverDate("");
    const seed = parseYMDParts(pStart.date) || parseYMDParts(startMinParts.date);
    if (seed) {
      setViewYear(seed.y);
      setViewMonth(seed.m);
    }
    // Sync drafts only when the popover opens — not on every parent update mid-selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updatePos = useCallback(() => {
    const el = triggerRef.current || wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || r.right;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || r.bottom;
    const margin = 12;
    const gap = 6;
    const availableWidth = Math.max(0, viewportWidth - margin * 2);
    const width = Math.min(Math.max(280, Math.min(420, r.width)), availableWidth);
    const left = Math.min(Math.max(margin, r.left), Math.max(margin, viewportWidth - width - margin));
    const maxHeight = Math.max(0, viewportHeight - margin * 2);
    const panelHeight = Math.min(popRef.current?.scrollHeight || maxHeight, maxHeight);
    const belowTop = r.bottom + gap;
    const aboveTop = r.top - gap - panelHeight;
    const preferredTop =
      belowTop + panelHeight <= viewportHeight - margin || aboveTop < margin
        ? belowTop
        : aboveTop;
    const top = Math.min(
      Math.max(margin, preferredTop),
      Math.max(margin, viewportHeight - margin - panelHeight)
    );

    setPos((current) => {
      const next = { left, top, width, maxHeight };
      if (
        current &&
        current.left === next.left &&
        current.top === next.top &&
        current.width === next.width &&
        current.maxHeight === next.maxHeight
      ) {
        return current;
      }
      return next;
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePos();
    const frame = window.requestAnimationFrame(updatePos);
    return () => window.cancelAnimationFrame(frame);
  }, [open, updatePos, draftStartDate, draftEndDate, viewYear, viewMonth]);

  useEffect(() => {
    function onDoc(e) {
      const w = wrapRef.current;
      const p = popRef.current;
      if (!w) return;
      if (w.contains(e.target)) return;
      if (p && p.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDoc, true);
    return () => document.removeEventListener("mousedown", onDoc, true);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onReflow = () => updatePos();
    window.addEventListener("resize", onReflow);
    window.addEventListener("scroll", onReflow, true);
    return () => {
      window.removeEventListener("resize", onReflow);
      window.removeEventListener("scroll", onReflow, true);
    };
  }, [open, updatePos]);

  const minStartDate = startMinParts.date || "";

  const computedEndMin = useMemo(() => {
    if (!draftStartDate) return endMin || "";
    const startDT = buildLocalDT(draftStartDate, draftStartTime);
    if (!startDT) return endMin || "";
    return addHoursLocalInputInZone(startDT, 24 * minRentalDays, tz) || endMin || "";
  }, [draftStartDate, draftStartTime, endMin, minRentalDays, tz]);

  const isStartDateDisabled = useCallback(
    (dateStr) => {
      if (!dateStr) return true;
      if (minStartDate && compareYMD(dateStr, minStartDate) < 0) return true;
      return false;
    },
    [minStartDate]
  );

  const isEndDateDisabled = useCallback(
    (dateStr) => {
      if (!dateStr || !draftStartDate) return true;
      if (compareYMD(dateStr, draftStartDate) < 0) return true;
      const candidate = buildLocalDT(dateStr, draftEndTime);
      if (!candidate || !computedEndMin) return false;
      try {
        return fromZonedTime(candidate, tz).getTime() < fromZonedTime(computedEndMin, tz).getTime();
      } catch {
        return false;
      }
    },
    [draftStartDate, draftEndTime, computedEndMin, tz]
  );

  const buildStart = useCallback(
    (date, time) => {
      if (!date) return "";
      return clampToMinInZone(buildLocalDT(date, time), startMin, tz);
    },
    [startMin, tz]
  );

  const buildEnd = useCallback(
    (startLocal, endDate, endTime) => {
      if (!startLocal || !endDate) return "";
      const minEnd = addHoursLocalInputInZone(startLocal, 24 * minRentalDays, tz);
      return clampToMinInZone(buildLocalDT(endDate, endTime), minEnd, tz);
    },
    [minRentalDays, tz]
  );

  const onDayClick = (dateStr) => {
    if (!dateStr) return;

    if (!draftStartDate || (draftStartDate && draftEndDate)) {
      if (isStartDateDisabled(dateStr)) return;
      setDraftStartDate(dateStr);
      setDraftEndDate("");
      const nextStart = buildStart(dateStr, draftStartTime);
      if (nextStart) onStartChange(nextStart);
      return;
    }

    if (compareYMD(dateStr, draftStartDate) < 0) {
      if (isStartDateDisabled(dateStr)) return;
      setDraftStartDate(dateStr);
      setDraftEndDate("");
      const nextStart = buildStart(dateStr, draftStartTime);
      if (nextStart) onStartChange(nextStart);
      return;
    }

    if (isEndDateDisabled(dateStr)) return;
    setDraftEndDate(dateStr);
    const nextStart = buildStart(draftStartDate, draftStartTime);
    const nextEnd = buildEnd(nextStart, dateStr, draftEndTime);
    if (nextStart) onStartChange(nextStart);
    if (nextEnd) onEndChange(nextEnd);
  };

  const previewEndDate = useMemo(() => {
    if (draftEndDate) return draftEndDate;
    if (draftStartDate && hoverDate && compareYMD(hoverDate, draftStartDate) >= 0) return hoverDate;
    return "";
  }, [draftEndDate, draftStartDate, hoverDate]);

  const calendarCells = useMemo(() => {
    const totalDays = daysInMonth(viewYear, viewMonth);
    const leading = startWeekdayMon(viewYear, viewMonth);
    const cells = [];

    for (let i = 0; i < leading; i++) cells.push(null);
    for (let d = 1; d <= totalDays; d++) {
      cells.push(ymdFromParts(viewYear, viewMonth, d));
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewYear, viewMonth]);

  const shiftMonth = (delta) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 0) {
      m += 12;
      y -= 1;
    }
    while (m > 11) {
      m -= 12;
      y += 1;
    }
    setViewYear(y);
    setViewMonth(m);
  };

  const onStartTimeChange = (time) => {
    setDraftStartTime(time);
    if (!draftStartDate) return;
    const nextStart = buildStart(draftStartDate, time);
    if (nextStart) onStartChange(nextStart);
  };

  const onEndTimeChange = (time) => {
    setDraftEndTime(time);
    const endDate = draftEndDate || endParts.date;
    if (!draftStartDate || !endDate) return;
    const nextStart = buildStart(draftStartDate, draftStartTime);
    const nextEnd = buildEnd(nextStart, endDate, time);
    if (nextEnd) onEndChange(nextEnd);
  };

  const displayText = useMemo(() => {
    if (!safeStr(startValue) && !safeStr(endValue)) return "Select dates…";
    if (safeStr(startValue) && safeStr(endValue)) {
      return `${formatDateCompact(startValue, tz)} → ${formatDateCompact(endValue, tz)}`;
    }
    return safeStr(startValue) ? formatDateCompact(startValue, tz) : "Select dates…";
  }, [startValue, endValue, tz]);

  const startMinTimeSameDay = startMinParts.time || "";

  const popover =
    open && !disabled && pos
      ? createPortal(
          <div
            className="rf-shell rf-popover-menu rf-date-range-popover"
            ref={popRef}
            onMouseDown={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={`${labelText || "Trip dates"} calendar`}
            style={{
              position: "fixed",
              left: pos.left,
              top: pos.top,
              width: pos.width,
              zIndex: 999999,
              border: `1px solid ${RF.line}`,
              borderRadius: 8,
              background: RF.surface,
              boxShadow: RF.shadowMd,
              overflow: "auto",
              maxHeight: pos.maxHeight,
              overscrollBehavior: "contain",
            }}
          >
            <div className="rf-date-range-body">
              <div className="rf-date-range-tz">Timezone: {tz}</div>
              <div className="rf-mini-calendar">
                <div className="rf-mini-calendar-header">
                  <button type="button" className="rf-mini-cal-nav" onClick={() => shiftMonth(-1)} aria-label="Previous month">
                    <ChevronLeft size={14} />
                  </button>
                  <div className="rf-mini-calendar-title">
                    {MONTH_NAMES[viewMonth]} {viewYear}
                  </div>
                  <button type="button" className="rf-mini-cal-nav" onClick={() => shiftMonth(1)} aria-label="Next month">
                    <ChevronRight size={14} />
                  </button>
                </div>

                <div className="rf-mini-calendar-weekdays">
                  {WEEKDAY_LABELS.map((d, i) => (
                    <div key={`${d}-${i}`} className="rf-mini-calendar-weekday">{d}</div>
                  ))}
                </div>

                <div className="rf-mini-calendar-grid">
                  {calendarCells.map((dateStr, idx) => {
                    if (!dateStr) {
                      return <div key={`empty-${idx}`} className="rf-mini-calendar-day is-empty" />;
                    }

                    const isStart = dateStr === draftStartDate;
                    const isEnd = dateStr === draftEndDate;
                    const inRange =
                      draftStartDate &&
                      previewEndDate &&
                      isBetweenYMD(dateStr, draftStartDate, previewEndDate);
                    const pickingEnd = draftStartDate && !draftEndDate;
                    const dayDisabled = pickingEnd ? isEndDateDisabled(dateStr) : isStartDateDisabled(dateStr);

                    let cellClass = "rf-mini-calendar-day";
                    if (dayDisabled) cellClass += " is-disabled";
                    if (isStart) cellClass += " is-start";
                    if (isEnd) cellClass += " is-end";
                    if (inRange && !isStart && !isEnd) cellClass += " is-in-range";
                    if (isStart && isEnd) cellClass += " is-single";

                    const dayNum = parseYMDParts(dateStr)?.d;

                    return (
                      <button
                        key={dateStr}
                        type="button"
                        className={cellClass}
                        disabled={dayDisabled}
                        aria-label={`${MONTH_NAMES[viewMonth]} ${dayNum}, ${viewYear}`}
                        aria-pressed={isStart || isEnd}
                        onMouseEnter={() => {
                          if (pickingEnd && !isEndDateDisabled(dateStr)) setHoverDate(dateStr);
                        }}
                        onMouseLeave={() => setHoverDate("")}
                        onClick={() => onDayClick(dateStr)}
                      >
                        <span>{dayNum}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rf-date-range-times">
                <label className="rf-date-range-time-field">
                  <span>From</span>
                  <input
                    type="time"
                    value={draftStartTime}
                    min={
                      draftStartDate && minStartDate && draftStartDate === minStartDate
                        ? startMinTimeSameDay || undefined
                        : undefined
                    }
                    onChange={(e) => onStartTimeChange(e.target.value)}
                  />
                </label>
                <label className="rf-date-range-time-field">
                  <span>To</span>
                  <input
                    type="time"
                    value={draftEndTime}
                    onChange={(e) => onEndTimeChange(e.target.value)}
                  />
                </label>
              </div>

              <div className="rf-date-range-hint">
                {!draftStartDate
                  ? "Pick start, then end"
                  : !draftEndDate && !previewEndDate
                    ? `Min ${minRentalDays} days`
                    : `${formatDateCompact(buildLocalDT(draftStartDate, draftStartTime), tz)} → ${formatDateCompact(buildLocalDT(draftEndDate || previewEndDate, draftEndTime), tz)}`}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <div style={{ display: "grid", gap: 6 }} ref={wrapRef}>
      {labelText ? <div style={label}>{labelText}</div> : null}

      <button
        ref={triggerRef}
        className="rf-select-trigger rf-date-range-trigger"
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        style={{
          ...inputStyle,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          textAlign: "left",
          paddingRight: 10,
          height: 44,
        }}
      >
        <span style={{ color: safeStr(startValue) ? RF.ink : RF.subtle, fontWeight: 850, fontSize: 12, lineHeight: 1.25 }}>
          {displayText}
        </span>
        <CalendarDays size={14} style={{ opacity: 0.8, flexShrink: 0 }} />
      </button>

      {popover}
    </div>
  );
}

/* ========================= tiny ui bits ========================= */
export function Divider({ style }) {
  return <div className="rf-divider" style={{ height: 1, background: RF.line, ...style }} />;
}

export function SpecChip({ children, tone = "neutral", style, className = "" }) {
  const toneMap = {
    neutral: { bg: "#f8fafc", bd: RF.line, fg: RF.ink },
    dark: { bg: RF.ink, bd: RF.ink, fg: "#ffffff" },
    good: { bg: RF.greenSoft, bd: RF.greenLine, fg: "#2f5f1f" },
    warn: { bg: RF.warnBg, bd: RF.warnLine, fg: RF.warnText },
  };
  const t = toneMap[tone] || toneMap.neutral;
  return (
    <span
      className={`rf-chip ${className}`.trim()}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        border: `1px solid ${t.bd}`,
        background: t.bg,
        color: t.fg,
        fontSize: 12,
        fontWeight: 900,
        whiteSpace: "nowrap",
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function Notice({ tone = "success", title, children }) {
  const tones = {
    success: { bd: RF.greenLine, bg: RF.greenSoft, fg: "#2f5f1f" },
    error: { bd: RF.dangerLine, bg: RF.dangerBg, fg: RF.dangerText },
    info: { bd: RF.greenLine, bg: "#f5fbf1", fg: "#236334" },
    warn: { bd: RF.warnLine, bg: RF.warnBg, fg: RF.warnText },
  };
  const t = tones[tone] || tones.success;
  return (
    <div
      className={`rf-notice rf-notice--${tone}`}
      role={tone === "error" ? "alert" : "status"}
      style={{
        marginTop: 12,
        borderRadius: 8,
        border: `1px solid ${t.bd}`,
        background: t.bg,
        color: t.fg,
        padding: 14,
        boxShadow: RF.shadowSm,
      }}
    >
      <div style={{ fontWeight: 950, fontSize: 13 }}>{title}</div>
      <div style={{ marginTop: 6, fontSize: 13, lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

export function LocationModeToggle({ value, onChange, deliveryFeeLabel, disabled = false }) {
  return (
    <div className="rf-segment" role="tablist" aria-label="Location type">
      <button
        type="button"
        role="tab"
        aria-selected={value === "FREE"}
        disabled={disabled}
        className={`rf-segment-btn ${value === "FREE" ? "is-active" : ""}`}
        onClick={() => onChange("FREE")}
      >
        From list (free)
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={value === "DELIVERY"}
        disabled={disabled}
        className={`rf-segment-btn ${value === "DELIVERY" ? "is-active" : ""}`}
        onClick={() => onChange("DELIVERY")}
      >
        Delivery ({deliveryFeeLabel})
      </button>
    </div>
  );
}

export function Stepper({ step, setStep, canGo2, canGo3, canGo4, canGo5 }) {
  const progress = ((Math.max(1, Math.min(5, Number(step) || 1)) - 1) / 4) * 100;

  const steps = [
    { n: 1, label: "Trip", short: "Trip", enabled: true },
    { n: 2, label: "Car", short: "Car", enabled: canGo2 },
    { n: 3, label: "Options", short: "Options", enabled: canGo3 },
    { n: 4, label: "Details", short: "Details", enabled: canGo4 },
    { n: 5, label: "Review", short: "Review", enabled: canGo5 },
  ];

  const current = steps.find((s) => s.n === step) || steps[0];

  return (
    <div className="rf-stepper">
      <div className="rf-step-mobile-label" aria-live="polite">
        Step {step} of 5 · {current.label}
      </div>

      <div className="rf-stepper-row">
        {steps.map((s) => (
          <button
            key={s.n}
            className={`rf-step ${step === s.n ? "is-active" : ""} ${step > s.n ? "is-complete" : ""} ${s.enabled ? "" : "is-disabled"}`}
            type="button"
            onClick={() => s.enabled && setStep(s.n)}
            aria-label={`Step ${s.n}: ${s.label}`}
            aria-disabled={!s.enabled}
            aria-current={step === s.n ? "step" : undefined}
            disabled={!s.enabled}
          >
            <span className="rf-step-number" aria-hidden="true">
              {step > s.n ? <CheckCircle2 size={15} /> : s.n}
            </span>
            <span className="rf-step-copy">
              <span className="rf-step-label-full">{s.label}</span>
              <span className="rf-step-label-short">{s.short}</span>
            </span>
          </button>
        ))}
      </div>

      <div className="rf-step-progress" aria-hidden="true">
        <span style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

/* ========================= GroupAvailabilityCard ========================= */
export function GroupAvailabilityCard({
  groupName,
  variants,
  carImageUrls,
  isOpen,
  isMobile,
  selectedCar,
  setSelectedCar,
  onToggle,
  rentalDays,
  priceSummary,
  currencyCode,
  formatMoney: fmt,
  onBack,
  onContinue,
  canContinue,
  countryCode,
}) {
  const formRef = useRef(null);
  const [openH, setOpenH] = useState(0);

  const [prefTransmission, setPrefTransmission] = useState("ANY");
  const [prefFuel, setPrefFuel] = useState("ANY");
  const [prefBody, setPrefBody] = useState("ANY");

  useLayoutEffect(() => {
    if (!isOpen) {
      setOpenH(0);
      return;
    }
    const el = formRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => setOpenH(el.scrollHeight || 0));
    return () => cancelAnimationFrame(raf);
  }, [isOpen, prefTransmission, prefFuel, prefBody, selectedCar, isMobile]);

  useEffect(() => {
    if (!isOpen) return;
    const onResize = () => {
      const el = formRef.current;
      if (!el) return;
      setOpenH(el.scrollHeight || 0);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [isOpen]);

  const transmissions = useMemo(
    () => uniq(variants.map((v) => safeStr(v.transmission))).filter(Boolean).sort(),
    [variants]
  );
  const fuels = useMemo(
    () => uniq(variants.map((v) => safeStr(v.fuelType))).filter(Boolean).sort(),
    [variants]
  );
  const bodies = useMemo(
    () => uniq(variants.map((v) => safeStr(v.seats))).filter(Boolean).sort(),
    [variants]
  );
  const selectedIsInGroup = useMemo(() => {
    if (!selectedCar) return false;
    return variants.some((v) => v?.id === selectedCar.id);
  }, [variants, selectedCar]);

  const matchesPrefs = (v, prefs) => {
    const t = safeStr(v?.transmission);
    const f = safeStr(v?.fuelType);
    const b = safeStr(v?.bodyType);
    if (prefs.t !== "ANY" && t !== prefs.t) return false;
    if (prefs.f !== "ANY" && f !== prefs.f) return false;
    if (prefs.b !== "ANY" && b !== prefs.b) return false;
    return true;
  };

  const feasibleTransmissions = useMemo(() => {
    return uniq(
      variants
        .filter((v) => matchesPrefs(v, { t: "ANY", f: prefFuel, b: prefBody }))
        .map((v) => safeStr(v?.transmission))
    ).filter(Boolean);
  }, [variants, prefFuel, prefBody]);

  const feasibleFuels = useMemo(() => {
    return uniq(
      variants
        .filter((v) => matchesPrefs(v, { t: prefTransmission, f: "ANY", b: prefBody }))
        .map((v) => safeStr(v?.fuelType))
    ).filter(Boolean);
  }, [variants, prefTransmission, prefBody]);

  const feasibleBodies = useMemo(() => {
    return uniq(
      variants
        .filter((v) => matchesPrefs(v, { t: prefTransmission, f: prefFuel, b: "ANY" }))
        .map((v) => safeStr(v?.bodyType))
    ).filter(Boolean);
  }, [variants, prefTransmission, prefFuel]);

  useEffect(() => {
    if (!isOpen) return;
    if (prefTransmission !== "ANY" && !feasibleTransmissions.includes(prefTransmission)) setPrefTransmission("ANY");
    if (prefFuel !== "ANY" && !feasibleFuels.includes(prefFuel)) setPrefFuel("ANY");
    if (prefBody !== "ANY" && !feasibleBodies.includes(prefBody)) setPrefBody("ANY");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, feasibleTransmissions, feasibleFuels, feasibleBodies]);

  const matchedVariant = useMemo(() => {
    const list = variants.filter((v) => matchesPrefs(v, { t: prefTransmission, f: prefFuel, b: prefBody }));
    return list[0] || null;
  }, [variants, prefTransmission, prefFuel, prefBody]);

  useEffect(() => {
    if (!isOpen) return;
    if (!matchedVariant) {
      if (selectedCar && variants.some((v) => v?.id === selectedCar.id)) setSelectedCar(null);
      return;
    }
    if (!selectedCar || selectedCar.id !== matchedVariant.id) setSelectedCar(matchedVariant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, matchedVariant, variants]);

  const repImg = useMemo(() => {
    if (matchedVariant) {
      const url = carImageUrls?.[matchedVariant.id];
      if (url) return url;
    }
    const withUrl = variants.find((v) => carImageUrls?.[v.id]);
    return withUrl ? carImageUrls[withUrl.id] : null;
  }, [matchedVariant, variants, carImageUrls]);

  const depositHint = useMemo(() => {
    const v = matchedVariant || selectedCar;
    return calcPolicyDeposit(countryCode, v?.category); // {amount,currency} | null
  }, [countryCode, matchedVariant, selectedCar]);

  const depositHintAmount = depositHint?.amount;
  const depositHintCurrency = depositHint?.currency;
  const hasDepositHint = Number.isFinite(Number(depositHintAmount));
  const displayGroupName = useMemo(() => {
    const baseCar = matchedVariant || variants[0] || {};
    return formatCarNameWithoutYear({ ...baseCar, name: groupName }, groupName || "Unnamed");
  }, [groupName, matchedVariant, variants]);

  return (
    <div className="rf-group-card" style={{ borderRadius: 8 }}>
      <button
        className="rf-group-card-toggle rf-card-elevate"
        type="button"
        onClick={onToggle}
        style={{
          borderRadius: 8,
          border: `1px solid ${isOpen ? RF.greenMid : RF.line}`,
          background: isOpen
            ? "linear-gradient(180deg, rgba(239,251,233,0.98), rgba(255,255,255,0.98))"
            : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,247,0.96))",
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
          padding: 14,
          boxShadow: isOpen ? "0 18px 44px rgba(47,143,70,0.16)" : RF.shadowSm,
          transition: "transform 120ms ease, box-shadow 120ms ease",
        }}
      >
        <div
          className="rf-vehicle-card-layout"
          style={{
            display: "flex",
            gap: 12,
            alignItems: isMobile ? "stretch" : "center",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <div
            className="rf-vehicle-media"
            style={{
              width: isMobile ? "100%" : 200,
              height: isMobile ? 190 : 122,
              borderRadius: 8,
              overflow: "hidden",
              border: `1px solid ${RF.line}`,
              background: "#f3f4f6",
              flexShrink: 0,
              position: "relative",
            }}
          >
            {repImg ? (
              <img
                className="rf-group-card-image"
                src={repImg}
                alt={displayGroupName}
                decoding="async"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                onError={(e) => (e.currentTarget.style.display = "none")}
              />
            ) : (
              <div style={{ display: "grid", placeItems: "center", height: "100%", color: "#6b7280", fontWeight: 950, fontSize: 12 }}>
                No photo
              </div>
            )}
          </div>

          <div className="rf-vehicle-card-body" style={{ flex: 1, minWidth: 0 }}>
            <div className="rf-vehicle-title-row" style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
              <div style={{ fontWeight: 950, color: RF.ink, fontSize: 15, minWidth: 0 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0 }}>
                  <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {displayGroupName}
                  </span>
                  {selectedIsInGroup ? (
                    <SpecChip tone="good" style={{ padding: "3px 9px", fontSize: 11 }}>
                      <CheckCircle2 size={12} /> Picked
                    </SpecChip>
                  ) : null}
                </div>
              </div>

              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, color: RF.ink, fontWeight: 950, fontSize: 12 }}>
                {isOpen ? "Close" : "Select"}
                <ChevronDown
                  size={16}
                  style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 180ms ease" }}
                />
              </div>
            </div>

            <div className="rf-vehicle-specs" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
              <SpecChip>{rentalDays ? `${rentalDays} day(s)` : "Days —"}</SpecChip>

              {priceSummary?.loading ? (
                <SpecChip tone="warn">Computing price…</SpecChip>
              ) : priceSummary?.fromTotal != null ? (
                <>
                  <SpecChip tone="good">
                    Total now: <b>{fmt(priceSummary.fromTotal, priceSummary.currency || currencyCode)}</b>
                  </SpecChip>
                  {priceSummary?.hasDiscount && priceSummary?.fromOriginalTotal != null ? (
                    <>
                      <SpecChip tone="neutral" className="rf-chip-previous">
                        Was:{" "}
                        <span style={{ textDecoration: "line-through", opacity: 0.72 }}>
                          {fmt(priceSummary.fromOriginalTotal, priceSummary.currency || currencyCode)}
                        </span>
                      </SpecChip>
                      <SpecChip tone="good" className="rf-chip-discount">10% off rental online</SpecChip>
                    </>
                  ) : null}
                </>
              ) : (
                <SpecChip tone="warn">No quote yet</SpecChip>
              )}

              {hasDepositHint ? (
                <SpecChip tone="neutral">
                  Policy deposit: <b>{fmt(Number(depositHintAmount), depositHintCurrency || "USD")}</b>
                </SpecChip>
              ) : null}

              {priceSummary?.err ? <SpecChip tone="warn">Quote error</SpecChip> : null}

              <SpecChip>
                {transmissions.length === 1 ? transmissions[0] : `Trans: ${transmissions.slice(0, 3).join(" · ")}`}
              </SpecChip>
              <SpecChip>{fuels.length === 1 ? fuels[0] : `Fuel: ${fuels.slice(0, 3).join(" · ")}`}</SpecChip>
              <SpecChip>{bodies.length === 1 ? `Seats: ${bodies[0]}` : `Seats: ${bodies.slice(0, 3).join(" · ")}`}</SpecChip>
            </div>

            <div className="rf-vehicle-availability" style={{ marginTop: 10, fontSize: 12, color: RF.muted }}>Available for your dates.</div>
          </div>
        </div>
      </button>

      <div
        ref={formRef}
        style={{
          overflow: "hidden",
          maxHeight: isOpen ? `${openH}px` : 0,
          opacity: isOpen ? 1 : 0,
          transform: isOpen ? "translateY(0)" : "translateY(-6px)",
          transition: "max-height 260ms ease, opacity 180ms ease, transform 180ms ease",
          borderStyle: "solid",
          borderColor: isOpen ? RF.line : "transparent",
          borderWidth: "0 1px 1px 1px",
          borderBottomLeftRadius: 8,
          borderBottomRightRadius: 8,
          background: RF.surface,
        }}
      >
        {isOpen && (
          <div className="rf-vehicle-configurator" style={{ padding: 14 }}>
            <div style={{ fontWeight: 950, color: RF.ink }}>Choose options</div>
            <div style={{ fontSize: 12, color: RF.muted, marginTop: 4 }}>
              We’ll match an available vehicle in this group based on your selection.
            </div>

            <div className="rf-vehicle-config-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, marginTop: 12 }}>
              <SelectPopover
                labelText="Transmission"
                value={prefTransmission}
                onChange={setPrefTransmission}
                options={[{ value: "ANY", label: "Any" }, ...feasibleTransmissions.map((t) => ({ value: t, label: t }))]}
                searchable={false}
              />

              <SelectPopover
                labelText="Fuel type"
                value={prefFuel}
                onChange={setPrefFuel}
                options={[{ value: "ANY", label: "Any" }, ...feasibleFuels.map((f) => ({ value: f, label: f }))]}
                searchable={false}
              />

              <SelectPopover
                labelText="Body type"
                value={prefBody}
                onChange={setPrefBody}
                options={[{ value: "ANY", label: "Any" }, ...feasibleBodies.map((b) => ({ value: b, label: b }))]}
                searchable={false}
              />

              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <div style={{ fontSize: 12, color: RF.muted }}>
                  {selectedCar ? (
                    <>Matched: <b style={{ color: "#111827" }}>{formatCarNameWithoutYear(selectedCar)}</b></>
                  ) : (
                    "No match yet."
                  )}
                </div>
              </div>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={onBack} className="rf-cta rf-cta-secondary" style={ctaSecondary}>
                Back
              </button>
              <button
                type="button"
                disabled={!canContinue}
                onClick={onContinue}
                className="rf-cta rf-cta-primary"
                style={{
                  ...ctaPrimary,
                  opacity: canContinue ? 1 : 0.55,
                  cursor: canContinue ? "pointer" : "not-allowed",
                }}
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ========================= styles exported ========================= */
export const outerWrap = {
  width: "100%",
  padding: "18px 14px",
  background: "transparent",
};

export const panel = {
  width: "100%",
  margin: 0,
  padding: 0,
  borderRadius: 0,
  border: "none",
  background: "transparent",
  boxShadow: "none",
};

export const panelHeader = { paddingBottom: 14, borderBottom: `1px solid ${RF.line}`, marginBottom: 12 };

export const iconBadge = {
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  border: `1px solid ${RF.greenLine}`,
  background: "linear-gradient(180deg, #f7fff3, #ffffff)",
  boxShadow: RF.shadowSm,
  color: RF.greenText,
};

export const grid2 = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
  marginTop: 0,
};

export const label = { display: "grid", gap: 8, fontSize: 12, color: RF.ink, fontWeight: 800 };

export const inputStyle = {
  width: "100%",
  minHeight: 50,
  borderRadius: 12,
  border: `1px solid ${RF.line}`,
  padding: "0 12px",
  fontSize: 14,
  outline: "none",
  background: "#ffffff",
  boxShadow: "0 1px 0 rgba(15,23,42,0.04)",
  display: "block",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  transition: "border-color 180ms ease, box-shadow 180ms ease",
};

export const btnPrimary = {
  width: "100%",
  height: 44,
  padding: "0 14px",
  borderRadius: 999,
  background: RF.green,
  border: `1px solid ${RF.greenMid}`,
  color: RF.ink,
  fontWeight: 800,
  fontSize: 13,
  cursor: "pointer",
  boxShadow: "0 12px 26px rgba(72,96,31,0.15)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  transition: "transform 180ms ease, box-shadow 180ms ease, filter 180ms ease",
};

export const alertBad = {
  marginTop: 12,
  borderRadius: 8,
  padding: 12,
  background: "#fee2e2",
  border: "1px solid #fecaca",
  color: "#7f1d1d",
  fontSize: 13,
};

export const ctaPrimary = {
  padding: "11px 16px",
  borderRadius: 999,
  background: RF.green,
  border: `1px solid ${RF.greenMid}`,
  color: RF.ink,
  fontWeight: 800,
  fontSize: 14,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  boxShadow: "0 12px 26px rgba(72,96,31,0.15)",
  transition: "transform 180ms ease, box-shadow 180ms ease, filter 180ms ease",
};

export const ctaSecondary = {
  padding: "10px 16px",
  borderRadius: 999,
  background: "#ffffff",
  border: `1px solid ${RF.line}`,
  color: RF.ink,
  fontWeight: 750,
  fontSize: 14,
  cursor: "pointer",
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
};

export const extrasGrid = {
  display: "grid",
  gap: 10,
  padding: 12,
  borderRadius: 8,
  border: `1px solid ${RF.line}`,
  background: "linear-gradient(180deg, #ffffff, #f9fafb)",
};

export const extraRow = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 13,
  color: RF.ink,
  fontWeight: 800,
};

export const extraNote = { fontSize: 12, fontWeight: 700, color: "#6b7280" };

export const qtyBtn = {
  width: 38,
  height: 40,
  borderRadius: 8,
  border: `1px solid ${RF.line}`,
  background: "#fff",
  fontWeight: 950,
  cursor: "pointer",
  boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
};

export const reviewCard = {
  marginTop: 0,
  border: `1px solid ${RF.line}`,
  borderRadius: 18,
  padding: 20,
  background: RF.surface,
  boxShadow: "none",
  transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
};

export const reviewGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 12,
};

export const reviewItem = {
  border: `1px solid ${RF.line}`,
  borderRadius: 14,
  padding: 14,
  background: RF.surfaceSoft,
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
};

export const reviewLabel = { fontSize: 10, fontWeight: 800, color: RF.subtle, textTransform: "uppercase", letterSpacing: "0.08em" };
export const reviewValue = { marginTop: 5, fontSize: 14, fontWeight: 700, color: RF.ink, lineHeight: 1.45, wordBreak: "break-word" };

export const priceRow = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
  padding: "10px 12px",
  borderRadius: 12,
  border: `1px solid ${RF.line}`,
  background: "#ffffff",
  transition: "transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease",
};

export const priceLeft = { fontWeight: 750, color: RF.muted };
export const priceRight = { fontWeight: 900, color: RF.ink };
